// API seam: Cloud Functions callables per ARCHITECTURE.md §3. Function names
// are the contract; `VITE_VK_MOCK=1` swaps in a local fake so the whole wizard
// is clickable before the Firebase project + vision/LLM/fal keys exist
// (blockers #20-#22). Real path calls the same names the functions scaffold will export.
import { httpsCallable } from "firebase/functions";
import { doc, collection, query, where, onSnapshot, addDoc, getDoc } from "firebase/firestore";
import { getDownloadURL, ref } from "firebase/storage";
import { auth, db, functions, storage } from "./firebase";
import { sanitizeImage } from "./image-sanitize";
import type { Extraction, Kit, KitArtifact, Kid } from "./types";

export const isMock = import.meta.env.VITE_VK_MOCK === "1";

// ---- intake ----
export async function extractWords(photoIds: string[]): Promise<Extraction> {
  if (isMock) {
    // Mock returns the m2_week1 fixture so the review step has realistic data.
    return {
      title: "1st Grade Weekly Words",
      grade: "1",
      week: "M2 W1",
      sections: [
        { name: "Spelling - On Level", words: ["pass", "mess", "buzz", "miss", "boss", "glass", "less", "guess"] },
        { name: "Sight Words", words: ["was", "said", "look", "little", "little", "there"] },
        { name: "Ready for More", words: ["she'll", "I've", "well-known", "yourself"] },
      ],
    };
  }
  const fn = httpsCallable<{ photoIds: string[] }, Extraction>(functions, "intakeExtract");
  const res = await fn({ photoIds });
  return res.data;
}

// ---- theme catalog + intake classifier (issue #14 / #15) ----
export type ThemeOption = { slug: string; name: string; world: string; redirectFor: string[] };
export type ClassifyResult = { verdict: "clean" | "ip" | "ambiguous"; redirectTheme: string | null; message: string | null };

export const THEME_CATALOG: ThemeOption[] = [
  { slug: "explorers", name: "The Explorers", world: "jungle, desert and sea quests", redirectFor: [] },
  { slug: "critter-camp", name: "Critter Camp", world: "a forest camp with original critters", redirectFor: [] },
  { slug: "space-scouts", name: "Space Scouts", world: "planets and missions", redirectFor: [] },
  { slug: "dino-rangers", name: "Dino Rangers", world: "a prehistoric valley", redirectFor: [] },
  { slug: "castle-friends", name: "Castle Friends", world: "a castle and village", redirectFor: [] },
  { slug: "ocean-pals", name: "Ocean Pals", world: "a coral reef and shipwreck", redirectFor: [] },
];

export async function classifyTheme(text: string): Promise<ClassifyResult> {
  if (isMock) return { verdict: "clean", redirectTheme: null, message: null };
  const fn = httpsCallable<{ text: string }, ClassifyResult>(functions, "themeClassify");
  const res = await fn({ text });
  return res.data;
}

// ---- kits ----
export async function createKit(kidId: string, extraction: Extraction | null, theme: string,
  bookletFormat: "saddle" | "flat" = "saddle", duplicateOf?: string,
  regenOf?: string, kinds?: string[]): Promise<string> {
  if (isMock) return mockCreateKit(kidId,
    extraction ?? mockKits.get(duplicateOf ?? regenOf ?? "")?.extraction ?? { title: "", sections: [] },
    kinds, regenOf);
  const fn = httpsCallable<{ kidId: string; extraction?: Extraction; theme: string; bookletFormat: string; duplicateOf?: string; regenOf?: string; kinds?: string[] }, { kitId: string }>(
    functions,
    "kitGenerate",
  );
  // UNAVAILABLE is a transport blip (dropped connection) — the request
  // usually never reached the server, so one quick retry is safe.
  for (let attempt = 0; ; attempt++) {
    try {
      const res = await fn({ kidId, extraction: extraction ?? undefined, theme, bookletFormat, duplicateOf, regenOf, kinds });
      return res.data.kitId;
    } catch (e) {
      const code = (e as { code?: string })?.code ?? "";
      if (code !== "functions/unavailable" || attempt >= 1) throw e;
      await new Promise((r) => setTimeout(r, 1500));
    }
  }
}

// One-shot kit fetch — duplicate flow reads the source kit's stored
// extraction/theme without subscribing to a stream.
export async function getKitOnce(kitId: string): Promise<Kit | null> {
  if (isMock) return mockKits.get(kitId) ?? null;
  const s = await getDoc(doc(db, "kits", kitId));
  return s.exists() ? ({ id: s.id, ...s.data() } as Kit) : null;
}

export function watchKit(kitId: string, cb: (kit: Kit | null) => void): () => void {
  if (isMock) return mockWatchKit(kitId, cb);
  return onSnapshot(doc(db, "kits", kitId), (s) => cb(s.exists() ? ({ id: s.id, ...s.data() } as Kit) : null));
}

export function watchKitsForKid(kidId: string, cb: (kits: Kit[]) => void): () => void {
  if (isMock) return mockWatchKits(kidId, cb);
  // Rules require the owner clause to be visible in the query itself.
  const uid = auth.currentUser?.uid;
  if (!uid) { cb([]); return () => {}; }
  const q = query(collection(db, "kits"), where("owner", "==", uid), where("kidId", "==", kidId));
  return onSnapshot(q, (s) => cb(s.docs.map((d) => ({ id: d.id, ...d.data() }) as Kit)));
}

// #39 "generate another": variants of one module (regen.of == rootKitId),
// newest first. Kid-scoped query (rules-friendly), filtered client-side.
export function watchVariantsOf(kidId: string, rootKitId: string, cb: (kits: Kit[]) => void): () => void {
  return watchKitsForKid(kidId, (kits) =>
    cb(kits.filter((k) => k.regen?.of === rootKitId).sort((a, b) => b.createdAt - a.createdAt)));
}

export async function artifactUrl(a: { path: string }): Promise<string> {
  if (isMock) return Promise.reject(new Error("mock mode: no stored artifacts"));
  return getDownloadURL(ref(storage, a.path));
}

// ---- kids (parent-scoped rows; accounts are parents — SPEC.md) ----
export async function createKid(name: string, grade: string): Promise<string> {
  const uid = auth.currentUser?.uid;
  if (!uid) throw new Error("sign in first");
  if (isMock) return "kid-mock-1";
  const d = await addDoc(collection(db, "users", uid, "kids"), { name, grade, themes: [], createdAt: Date.now() });
  return d.id;
}

export function watchKids(cb: (kids: Kid[]) => void): () => void {
  if (isMock) {
    cb([{ id: "kid-mock-1", name: "Parker", grade: "1", themes: [] }]);
    return () => {};
  }
  const uid = auth.currentUser?.uid;
  if (!uid) {
    cb([]);
    return () => {};
  }
  return onSnapshot(collection(db, "users", uid, "kids"), (s) =>
    cb(s.docs.map((d) => ({ id: d.id, ...d.data() }) as Kid)),
  );
}

// ---- uploads (photos: EXIF-stripped client-side via canvas re-encode, see
// image-sanitize.ts; 7d bucket lifecycle on photos/ verified live; #18) ----
export async function uploadPhoto(file: File): Promise<string> {
  if (isMock) return `photos/mock-${Date.now()}`;
  const uid = auth.currentUser?.uid;
  if (!uid) throw new Error("sign in first");
  const id = `${uid}/${Date.now()}-${file.name.replace(/[^\w.\-]/g, "_")}`;
  const clean = await sanitizeImage(file);
  await uploadToStorage(`photos/${id}`, clean);
  return id;
}

async function uploadToStorage(path: string, file: Blob) {
  const { uploadBytes } = await import("firebase/storage");
  await uploadBytes(ref(storage, path), file, { contentType: file.type || "image/jpeg" });
}

// ---- privacy (#18): right to erasure. Server-side hard cascade; see
// functions/src/delete-account.ts + docs/PRIVACY.md ----
export async function deleteAccount(): Promise<{ ok: boolean; deletedKits: number; stripe: string }> {
  if (isMock) return { ok: true, deletedKits: 0, stripe: "mock" };
  const fn = httpsCallable<undefined, { ok: boolean; deletedKits: number; stripe: string }>(functions, "deleteAccount");
  const res = await fn(undefined);
  return res.data;
}

// ---- billing (issue #12): plan comes from users/{uid}/billing/state,
// written ONLY by the stripe webhook (firestore.rules) — never client-writable.
export type Billing = {
  plan: "free" | "family" | "familyplus";
  status: string;
  interval: "month" | "year";
  currentPeriodEnd: number;
  hasCustomer: boolean;
};
const FREE_BILLING: Billing = { plan: "free", status: "none", interval: "month", currentPeriodEnd: 0, hasCustomer: false };

export function watchBilling(cb: (b: Billing) => void): () => void {
  if (isMock) { cb(FREE_BILLING); return () => {}; }
  const uid = auth.currentUser?.uid;
  if (!uid) { cb(FREE_BILLING); return () => {}; }
  return onSnapshot(doc(db, `users/${uid}/billing/state`), (s) => {
    const d = s.data() ?? {};
    cb({
      plan: (d.plan as Billing["plan"]) ?? "free",
      status: d.status ?? "none",
      interval: d.interval ?? "month",
      currentPeriodEnd: d.currentPeriodEnd ?? 0,
      hasCustomer: !!d.stripeCustomerId,
    });
  });
}

/** Returns the Stripe-hosted Checkout URL; caller redirects the browser. */
export async function createCheckout(plan: "family" | "familyplus", interval: "monthly" | "yearly"): Promise<string> {
  if (isMock) return Promise.reject(new Error("mock mode: billing disabled"));
  const fn = httpsCallable<{ plan: string; interval: string }, { url: string }>(functions, "stripeCheckoutCreate");
  const res = await fn({ plan, interval });
  return res.data.url;
}

export async function openPortal(): Promise<string> {
  if (isMock) return Promise.reject(new Error("mock mode: billing disabled"));
  const fn = httpsCallable<Record<string, never>, { url: string }>(functions, "stripePortalCreate");
  const res = await fn({});
  return res.data.url;
}

// ---- mock backend: exercises the whole UI against the same shapes ----
const mockKits = new Map<string, Kit>();

async function mockCreateKit(kidId: string, ex: Extraction, kinds?: string[], regenOf?: string): Promise<string> {
  const id = `kit-${Date.now()}`;
  const kit: Kit = {
    id, kidId, title: ex.title, week: ex.week ?? "", status: "queued",
    artifacts: [], wordCount: ex.sections.reduce((n, s) => n + new Set(s.words).size, 0),
    extraction: ex, // enables mock "make another copy" parity
    // #39: regen variants ship only the requested printables.
    ...(kinds && regenOf ? { regen: { of: regenOf, kinds } } : {}),
    createdAt: Date.now(),
  };
  mockKits.set(id, kit);
  // fake pipeline: queued -> building -> validating -> ready
  const advance = (status: Kit["status"]) => {
    kit.status = status;
    listeners.get(id)?.forEach((cb) => cb({ ...kit }));
  };
  setTimeout(() => advance("building"), 800);
  setTimeout(() => advance("validating"), 2200);
  setTimeout(() => {
    const all: KitArtifact[] = [
      { kind: "flashcards", path: `users/mock/kits/${id}/flashcards.pdf`, pages: 1 },
      { kind: "wordsearch", path: `users/mock/kits/${id}/word_search.pdf`, pages: 2 },
      { kind: "crossword", path: `users/mock/kits/${id}/crossword.pdf`, pages: 2 },
      { kind: "handwriting", path: `users/mock/kits/${id}/handwriting.pdf`, pages: 3 },
      { kind: "booklet", path: `users/mock/kits/${id}/booklet.pdf`, pages: 4 },
    ];
    kit.artifacts = kinds ? all.filter((a) => kinds.includes(a.kind)) : all;
    advance("ready");
  }, 3500);
  return id;
}

const listeners = new Map<string, Set<(kit: Kit) => void>>();

function mockWatchKit(kitId: string, cb: (kit: Kit | null) => void): () => void {
  const set = listeners.get(kitId) ?? new Set();
  set.add(cb as (kit: Kit) => void);
  listeners.set(kitId, set);
  cb(mockKits.get(kitId) ?? null);
  return () => set.delete(cb as (kit: Kit) => void);
}

function mockWatchKits(kidId: string, cb: (kits: Kit[]) => void): () => void {
  const all = [...mockKits.values()].filter((k) => k.kidId === kidId);
  cb(all);
  // re-notify on any status change (simplification: poll)
  const t = setInterval(() => cb([...mockKits.values()].filter((k) => k.kidId === kidId)), 1000);
  return () => clearInterval(t);
}

// ---- operator dashboard (#16/#17) ----
export interface UsageRollup {
  usage: { day: string; model: string; calls: number; images: number; estUsd: number }[];
  kits: { day: string; kits: number; ready: number; failed: number; inFlight: number; users: number; artifacts: number }[];
  totals: { users: number; paying: number; readyRate: number | null };
  targets: { photoToReadyPct: number; freeToPaidPct: number; week4RetentionPct: number };
  updatedAt?: unknown;
}
export async function getUsage(): Promise<UsageRollup> {
  if (isMock) return { usage: [], kits: [], totals: { users: 0, paying: 0, readyRate: null }, targets: { photoToReadyPct: 90, freeToPaidPct: 5, week4RetentionPct: 60 } };
  const fn = httpsCallable<Record<string, never>, UsageRollup>(functions, "usageSummary");
  const res = await fn({});
  return res.data;
}
